import { Injectable, computed, signal, inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BankExtractionRequestService, BankExtractionRequest } from '../services/bank-extraction-request.service';
import { Pagination, PaginationInput } from './types';
import { BankAccountsStore } from './bank-accounts.store';

export interface GetBankExtractionRequestsDto {
  search?: string;
  bankBeneficiaryId?: string;
  status?: string;
  currencyId?: string;
  paymentType?: string;
  importerId?: string;
}

@Injectable({
  providedIn: 'root'
})
export class BankExtractionRequestsStore {
  private bankExtractionRequestService = inject(BankExtractionRequestService);
  private bankAccountsStore = inject(BankAccountsStore);
  private matSnackBar = inject(MatSnackBar);

  private _bankExtractionRequests = signal<BankExtractionRequest[]>([]);
  private _loading = signal(false);
  private _error = signal<string | null>(null);
  private _loadingDescription = signal('');
  private _pagination = signal<Pagination>(null);
  private _paginationInput = signal<PaginationInput>({ page: 1, limit: 50 });
  private _currentFilter = signal<GetBankExtractionRequestsDto>({});

  // Public readonly signals
  public bankExtractionRequests = computed(() => this._bankExtractionRequests());
  public loading = computed(() => this._loading());
  public error = computed(() => this._error());
  public loadingDescription = computed(() => this._loadingDescription());
  public pagination = computed(() => this._pagination());
  public paginationInput = computed(() => this._paginationInput());
  public currentFilter = computed(() => this._currentFilter());

  /**
   * Load bank extraction requests with pagination and filtering
   */
  async loadBankExtractionRequests(filter: GetBankExtractionRequestsDto = {}): Promise<BankExtractionRequest[] | null> {
    // Merge with existing filter and account_number
    const mergedFilter = { 
      ...filter,
      accountNumber: this.bankAccountsStore.selectedBankAccountId() 
    };
    
    // Store the filter for future use (excluding account_number which is always added)
    this._currentFilter.set(filter);

    this._loading.set(true);
    this._error.set(null);
    this._loadingDescription.set('Cargando solicitudes de pago bancaria...');

    try {
      const response = await this.bankExtractionRequestService.getBankExtractionRequests(
        this._paginationInput(),
        mergedFilter
      );

      if (response.data?.elements) {
        const { elements, pagination } = response.data;

        // Only append if we're loading more pages (page > 1), otherwise replace
        if (this._paginationInput().page > 1 && this._bankExtractionRequests().length > 0) {
          this._bankExtractionRequests.set([...this._bankExtractionRequests(), ...elements]);
        } else {
          this._bankExtractionRequests.set(elements as BankExtractionRequest[]);
        }
        this._pagination.set(pagination);

        return this._bankExtractionRequests();
      } else if (response.error) {
        this.showLoadError(response.error?.message);
      }
    } catch (error) {
      this.showLoadError();
      console.error('Error loading bank extraction requests:', error);
    } finally {
      this._loading.set(false);
      this._loadingDescription.set('');
    }
    return null;
  }

  /**
   * Reload bank extraction requests from first page
   */
  reloadBankExtractionRequests(): void {
    this._paginationInput.set({ page: 1, limit: 50 });
    this._bankExtractionRequests.set([]);
    this.loadBankExtractionRequests(this._currentFilter());
  }

  /**
   * Load more bank extraction requests (next page)
   */
  loadMore(): void {
    if (this._pagination()?.hasNextPage) {
      this._paginationInput.update((current) => ({
        ...current,
        page: current.page + 1
      }));
      this.loadBankExtractionRequests(this._currentFilter());
    }
  }

  /**
   * Show error message
   */
  showLoadError(message?: string): void {
    this.matSnackBar.open(message || 'Error al cargar solicitudes de pago bancaria', 'Cerrar', {
      duration: 3000,
      panelClass: 'mat-snack-error'
    });
  }

  /**
   * Clear specific filter field and reload
   */
  clearFilterField(fieldName: keyof GetBankExtractionRequestsDto): void {
    const currentFilterValue = this._currentFilter();
    const newFilter = { ...currentFilterValue };
    delete newFilter[fieldName];
    this._currentFilter.set(newFilter);
    this.reloadBankExtractionRequests();
  }

  /**
   * Clear all filters and reload
   */
  clearAllFilters(): void {
    this._currentFilter.set({});
    this.reloadBankExtractionRequests();
  }

  /**
   * Clear all filters without reloading (used when navigating away or logging out)
   */
  clearFiltersOnly(): void {
    this._currentFilter.set({});
  }

  /**
   * Get bank extraction request by ID
   */
  getBankExtractionRequestById(id: string): BankExtractionRequest | undefined {
    return this._bankExtractionRequests().find(request => request._id === id);
  }

  /**
   * Add a new bank extraction request to the store
   */
  addBankExtractionRequest(request: BankExtractionRequest): void {
    this._bankExtractionRequests.update(current => [request, ...current]);
  }

  /**
   * Update an existing bank extraction request in the store
   */
  updateBankExtractionRequest(id: string, updatedRequest: Partial<BankExtractionRequest>): void {
    this._bankExtractionRequests.update(current =>
      current.map(request =>
        request._id === id ? { ...request, ...updatedRequest } : request
      )
    );
  }

  /**
   * Remove a bank extraction request from the store
   */
  removeBankExtractionRequest(id: string): void {
    this._bankExtractionRequests.update(current =>
      current.filter(request => request._id !== id)
    );
  }

  /**
   * Clear all data from the store
   */
  clearStore(): void {
    this._bankExtractionRequests.set([]);
    this._error.set(null);
    this._loading.set(false);
    this._loadingDescription.set('');
    this._pagination.set(null);
    this._currentFilter.set({});
    this._paginationInput.set({ page: 1, limit: 50 });
  }

  /**
   * Set loading state
   */
  setLoading(loading: boolean): void {
    this._loading.set(loading);
  }

  /**
   * Set error state
   */
  setError(error: string | null): void {
    this._error.set(error);
  }

  /**
   * Set loading description
   */
  setLoadingDescription(description: string): void {
    this._loadingDescription.set(description);
  }
}
